import { useTranslation } from 'react-i18next';
import { Sparkles, ShieldCheck, Wrench, FilePlus2 } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Card } from '../../components/ui/Card';
import { Reveal } from '../../components/landing/Reveal';

type EntryKind = 'feature' | 'security' | 'fix' | 'doc';
interface Entry {
  date: string;
  kind: EntryKind;
  title: string;
  body: string;
}

/**
 * Hand-maintained changelog. Each release that ships to production
 * gets a row here. Kept terse — the goal is "what did PlyEZ do this
 * month?" in one screen.
 */
const ENTRIES: Entry[] = [
  { date: '2026-05-23', kind: 'feature',
    title: 'Email + password auth, Sign in with Apple, public legal pages',
    body: 'Email/password registration with bcrypt + verification email. Sign in with Apple (alongside Google). Full /legal/* suite: Terms, Privacy, Cookie, DPA, Subprocessors, AUP.' },
  { date: '2026-05-23', kind: 'feature',
    title: 'tax-jp, tax-us, tax-eu modules',
    body: 'Live JP qualified-invoice + 法人番号 lookups, US W-9/W-8 collection + 1099 generator, EU VAT-ID validation (VIES) with reverse-charge resolver.' },
  { date: '2026-05-23', kind: 'feature',
    title: '13 connector modules',
    body: 'Accounting export to freee, MF, Yayoi, QuickBooks, Xero, NetSuite. Payment rails via Stripe (Issuing + Connect), Wise, GMO PG. JP bank statement ingestion for Mizuho / SMBC / MUFG / Rakuten / PayPay.' },
  { date: '2026-05-23', kind: 'feature',
    title: 'Close packet + admin verification queue',
    body: 'Print-ready monthly close packet at /app/.../close-packet with trial balance + hanko sign-off. Internal admin queue at /admin for verification reviewers.' },
  { date: '2026-05-23', kind: 'security',
    title: 'Tamper-evident audit log',
    body: 'Every workspace event is hash-chained: each event records SHA-256 of the previous one, so any deletion or backdated insertion breaks the chain on verify.' },
  { date: '2026-05-23', kind: 'feature',
    title: '14-language i18n with RTL',
    body: 'Hand-translated EN and JA; 12 more locales for AR, BN, DE, ES, FR, HI, ID, KO, PT, RU, UR, ZH. RTL flips Arabic and Urdu at the document level.' },
  { date: '2026-05-23', kind: 'feature',
    title: 'Whitish washi-paper design system',
    body: 'New visual identity: warm pearl background, sumi-ink-blue accent, cinnabar highlight, Noto Serif JP headlines, animated washi backdrop.' },
];

const KIND_META: Record<EntryKind, { icon: typeof Sparkles; tone: string; labelKey: string }> = {
  feature: { icon: Sparkles, tone: 'bg-accent-50 text-accent-700 dark:bg-accent-900 dark:text-accent-100', labelKey: 'changelog.kind.feature' },
  security: { icon: ShieldCheck, tone: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200', labelKey: 'changelog.kind.security' },
  fix: { icon: Wrench, tone: 'bg-warn-50 text-warn-600 dark:bg-warn-600/15 dark:text-amber-200', labelKey: 'changelog.kind.fix' },
  doc: { icon: FilePlus2, tone: 'bg-muted text-muted-foreground', labelKey: 'changelog.kind.doc' },
};

export function ChangelogPage() {
  const { t, i18n } = useTranslation();

  const grouped = ENTRIES.reduce<Record<string, Entry[]>>((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort((a, b) => (a > b ? -1 : 1));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12 lg:py-16">
        <Reveal as="div">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('changelog.eyebrow')}
          </span>
          <h1 className="display text-4xl lg:text-5xl font-semibold tracking-tight mt-4">{t('changelog.title')}</h1>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">{t('changelog.subtitle')}</p>
        </Reveal>

        <div className="rule mt-10" aria-hidden />

        <div className="mt-12 space-y-12">
          {dates.map((date) => (
            <Reveal as="section" key={date}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-1.5 w-1.5 rounded-full bg-cinnabar-500 shrink-0" aria-hidden />
                <div className="num text-sm font-medium text-muted-foreground">
                  {new Intl.DateTimeFormat(i18n.language, {
                    year: 'numeric', month: 'long', day: 'numeric',
                  }).format(new Date(date))}
                </div>
              </div>
              <div className="space-y-3 sm:pl-[1.4rem] sm:border-l sm:border-border">
                {grouped[date].map((e, i) => {
                  const Icon = KIND_META[e.kind].icon;
                  return (
                    <Card key={i} className="rounded-2xl border-border p-5 transition-shadow hover:shadow-soft">
                      <div className="flex items-start gap-3">
                        <div className={'h-8 w-8 rounded-xl flex items-center justify-center shrink-0 ' + KIND_META[e.kind].tone}>
                          <Icon size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              {t(KIND_META[e.kind].labelKey)}
                            </span>
                          </div>
                          <div className="font-medium">{e.title}</div>
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{e.body}</p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </Reveal>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
